// ─────────────────────────────────────────────────────────────────────────────────────────────
// carga.js — PERFIL DE CONSUMO y balance HORA A HORA contra la producción.
//
// Módulo PURO (sin red, sin React, sin imports relativos → `src/motores/`).
//
// 🔴 EL DEFECTO QUE CIERRA. Hasta ahora el autoconsumo se calculaba ANUALMENTE:
// `autoconsumo = min(producción_anual, consumo_anual)`. Eso da por hecho que un kWh generado al
// mediodía de junio compensa uno consumido a las 8 p.m. de diciembre — y no es así: a esa hora el
// arreglo no produce nada y esa energía se compra de la red.
//
// La diferencia NO es cosmética, porque los dos kWh valen distinto:
//   · autoconsumo → se valora al CU PLENO (deja de comprarse ese kWh).
//   · excedente   → se liquida a CU − G (AGPE Menor) o a precio de bolsa (Mayor). Siempre MENOS.
// Al netear anualmente, todo lo producido se cuenta como si desplazara consumo → el ahorro sale
// SOBREESTIMADO y el retorno, más corto de lo real. Para un residencial típico el autoconsumo real
// ronda 25-40% de lo producido, no el ~100% que sugiere el neteo anual.
//
// Este módulo hace el balance HORA A HORA. Con el perfil real de la telemetría es una medición;
// con el recibo de la luz es una estimación — y el resultado DICE cuál de las dos fue.

/**
 * Formas horarias de referencia (24 valores, normalizados: suman 1).
 *
 * ⚠️ Son perfiles de REFERENCIA por clase de usuario, no mediciones del cliente. Se usan cuando NO
 * hay telemetría, y todo resultado que se derive de ellos viaja marcado `fuente: 'recibo'`. Un
 * perfil de referencia presentado como si fuera el consumo real del cliente es la misma clase de
 * mentira que un PR de tabla en una memoria firmada.
 *
 * Residencial: valle nocturno, repunte de mañana y PICO marcado al anochecer (18-21 h) — el peor
 * caso para el solar, porque el pico no coincide con la generación.
 * Comercial: jornada 8-18 h, que es cuando el sol produce → autoconsumo alto.
 * Industrial: casi plano (turnos), con leve realce diurno.
 */
export const PERFILES_REFERENCIA = {
  residencial: [
    2.1, 1.8, 1.6, 1.5, 1.5, 1.9, 3.0, 3.8, 3.6, 3.2, 3.0, 3.1,
    3.4, 3.2, 3.0, 3.1, 3.6, 5.2, 8.4, 9.6, 8.8, 6.9, 4.8, 3.0,
  ],
  comercial: [
    1.2, 1.0, 1.0, 1.0, 1.0, 1.3, 2.4, 4.6, 6.8, 7.4, 7.6, 7.6,
    7.0, 7.4, 7.6, 7.4, 6.8, 5.4, 3.6, 2.4, 1.8, 1.5, 1.4, 1.3,
  ],
  industrial: [
    3.4, 3.3, 3.2, 3.2, 3.3, 3.6, 4.2, 4.8, 5.2, 5.4, 5.4, 5.3,
    4.9, 5.2, 5.4, 5.3, 5.0, 4.6, 4.2, 3.9, 3.7, 3.6, 3.5, 3.4,
  ],
};

/** Normaliza una forma de 24 h para que sume exactamente 1. */
function normalizar24(shape) {
  const s = shape.reduce((a, b) => a + (Number(b) || 0), 0);
  if (!(s > 0)) return new Array(24).fill(1 / 24);
  return shape.map((v) => (Number(v) || 0) / s);
}

function claseValida(c) {
  const k = String(c || '').toLowerCase();
  return PERFILES_REFERENCIA[k] ? k : 'residencial';
}

/**
 * Construye un perfil horario de 8760 h a partir del CONSUMO DEL RECIBO.
 *
 * @param {number} consumoMensualKwh — kWh/mes declarados (o promedio de la factura).
 * @param {object} opts — {clase: 'residencial'|'comercial'|'industrial', forma: number[24]}
 * @returns {{serie: number[], fuente: 'recibo', clase: string, anualKwh: number, nota: string}}
 */
export function perfilDesdeRecibo(consumoMensualKwh, opts = {}) {
  const mensual = Math.max(0, Number(consumoMensualKwh) || 0);
  const clase = claseValida(opts.clase);
  const forma = normalizar24(
    Array.isArray(opts.forma) && opts.forma.length === 24 ? opts.forma : PERFILES_REFERENCIA[clase]
  );
  const anual = mensual * 12;
  const diario = anual / 365;
  const serie = new Array(8760);
  for (let d = 0; d < 365; d++) {
    for (let h = 0; h < 24; h++) serie[d * 24 + h] = diario * forma[h];
  }
  return {
    serie,
    fuente: 'recibo',
    clase,
    anualKwh: anual,
    nota: `Perfil de referencia para uso ${clase}, escalado a ${Math.round(mensual).toLocaleString('es-CO')} kWh/mes. ` +
      `No es el consumo medido del cliente: para eso hace falta la telemetría del medidor.`,
  };
}

/**
 * Normaliza una serie de consumo MEDIDA (telemetría) a 8760 h.
 *
 * Acepta series de otra longitud (un mes, una semana) y las repite hasta cubrir el año, declarando
 * cuánta cobertura real tenía. Repetir una semana medida sigue siendo mejor que un perfil de
 * catálogo — pero el resultado lo DICE, no se presenta como un año medido.
 *
 * @param {number[]} serieKwh — consumo por hora, en kWh.
 * @returns {{serie, fuente:'telemetria', anualKwh, horasMedidas, coberturaPct, nota}|null}
 */
export function perfilDesdeTelemetria(serieKwh, opts = {}) {
  if (!Array.isArray(serieKwh) || serieKwh.length === 0) return null;
  const limpia = serieKwh.map((v) => {
    const n = Number(v);
    // Un consumo negativo no existe: o es un error de signo o es inyección de otro sistema. Se
    // descarta a 0 en vez de restarlo del consumo, que inventaría autoconsumo que no ocurrió.
    return Number.isFinite(n) && n > 0 ? n : 0;
  });
  if (limpia.every((v) => v === 0)) return null;

  const serie = new Array(8760);
  for (let i = 0; i < 8760; i++) serie[i] = limpia[i % limpia.length];
  const cobertura = Math.min(100, (limpia.length / 8760) * 100);
  return {
    serie,
    fuente: 'telemetria',
    anualKwh: serie.reduce((a, b) => a + b, 0),
    horasMedidas: limpia.length,
    coberturaPct: +cobertura.toFixed(1),
    nota: cobertura >= 99
      ? `Perfil MEDIDO del punto de consumo (${limpia.length.toLocaleString('es-CO')} h).`
      : `Perfil MEDIDO de ${limpia.length.toLocaleString('es-CO')} h (${cobertura.toFixed(0)}% del año), ` +
        `repetido para cubrir los 12 meses. La estacionalidad fuera del período medido no está capturada.`,
    ...opts,
  };
}

/**
 * BALANCE HORA A HORA entre producción y consumo.
 *
 * @param {number[]} produccionKwh — serie horaria de generación (la del motor, `horariaKwh`).
 * @param {number[]} consumoKwh — serie horaria de consumo (de `perfilDesdeRecibo`/`Telemetria`).
 * @returns objeto con autoconsumo, excedentes, importación y la comparación contra el neteo anual.
 */
export function balanceHorario(produccionKwh, consumoKwh) {
  const P = Array.isArray(produccionKwh) ? produccionKwh : [];
  const C = Array.isArray(consumoKwh) ? consumoKwh : [];
  const n = Math.min(P.length, C.length);
  if (n === 0) return null;

  let auto = 0, exced = 0, importado = 0;
  // Perfil medio de un día: para mostrar DÓNDE cae el desencuentro, que es lo que el cliente
  // entiende de un vistazo (produce al mediodía, consume de noche).
  const perfilProd = new Array(24).fill(0);
  const perfilCons = new Array(24).fill(0);
  const perfilAuto = new Array(24).fill(0);
  const dias = Math.max(1, Math.floor(n / 24));

  for (let i = 0; i < n; i++) {
    const p = Math.max(0, Number(P[i]) || 0);
    const c = Math.max(0, Number(C[i]) || 0);
    const a = Math.min(p, c);
    auto += a;
    exced += p - a;
    importado += c - a;
    const h = i % 24;
    perfilProd[h] += p; perfilCons[h] += c; perfilAuto[h] += a;
  }

  const prodTotal = auto + exced;
  const consTotal = auto + importado;

  // El neteo ANUAL — lo que se calculaba antes. Se computa para poder DECIR cuánto sobreestimaba.
  const autoNeteoAnual = Math.min(prodTotal, consTotal);

  return {
    autoconsumoKwh: Math.round(auto),
    excedentesKwh: Math.round(exced),
    importacionKwh: Math.round(importado),
    produccionKwh: Math.round(prodTotal),
    consumoKwh: Math.round(consTotal),
    // Qué fracción de lo PRODUCIDO se consume en el sitio. Es el número que decide el ahorro real.
    fraccionAutoconsumo: prodTotal > 0 ? +(auto / prodTotal).toFixed(4) : 0,
    // Qué fracción del CONSUMO cubre el sistema sin comprarle a la red (autosuficiencia).
    autosuficiencia: consTotal > 0 ? +(auto / consTotal).toFixed(4) : 0,
    horas: n,
    perfilMedioDia: perfilProd.map((_, h) => ({
      hora: h,
      produccion: +(perfilProd[h] / dias).toFixed(3),
      consumo: +(perfilCons[h] / dias).toFixed(3),
      autoconsumo: +(perfilAuto[h] / dias).toFixed(3),
    })),
    // La comparación honesta: cuánto autoconsumo suponía el método anterior de más.
    neteoAnual: {
      autoconsumoKwh: Math.round(autoNeteoAnual),
      sobrestimacionKwh: Math.round(autoNeteoAnual - auto),
      sobrestimacionPct: auto > 0 ? +(((autoNeteoAnual - auto) / auto) * 100).toFixed(1) : 0,
    },
  };
}

/**
 * Atajo de alto nivel: resuelve el perfil por la MEJOR fuente disponible y devuelve el balance.
 *
 * Precedencia declarada: telemetría medida → recibo de la luz. El resultado dice cuál se usó, para
 * que nadie lea una estimación como si fuera una medición.
 */
export function analizarConsumo(opts = {}) {
  const perfil = perfilDesdeTelemetria(opts.serieTelemetria)
    || perfilDesdeRecibo(opts.consumoMensualKwh, { clase: opts.clase, forma: opts.forma });
  const balance = balanceHorario(opts.produccionHoraria, perfil.serie);
  return { perfil: { ...perfil, serie: undefined }, balance, fuente: perfil.fuente };
}

export default {
  PERFILES_REFERENCIA, perfilDesdeRecibo, perfilDesdeTelemetria, balanceHorario, analizarConsumo,
};
